import {Component, Input, OnDestroy} from '@angular/core';
import {UIStateEnum} from "../../enum/UIStateEnum";
import {CharityService} from "../../services/charity.service";
import {concatMap, Observable, Subscription, tap} from "rxjs";
import {ICharitiesByCategoryResult} from "../../interfaces/charity/ICharitiesByCategoryResult";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {SnackbarService} from "../../services/snackbar.service";
import {IUpdateCharityCategorySortOrder} from "../../interfaces/charity/IUpdateCharityCategorySortOrder";
import {ICharityCategory} from "../../interfaces/charity/ICharityCategory";
import {CdkDrag, CdkDropList, CdkDropListGroup, moveItemInArray} from "@angular/cdk/drag-drop";
import {PermissionTypes} from "../../enum/PermissionTypes";
import {CharityCategoryViewComponent} from "../../components/charity-category-view/charity-category-view.component";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {AsyncPipe} from "@angular/common";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {ContainerComponent} from "@coreui/angular";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";

@Component({
  selector: 'app-manage-charities',
  standalone: true,
  templateUrl: './charity-management.component.html',
  imports: [
    MatIcon,
    CharityCategoryViewComponent,
    CdkDropList,
    MatButton,
    MatTooltip,
    CdkDropListGroup,
    AsyncPipe,
    MatProgressSpinner,
    HasPermissionDirective,
    CdkDrag,
    ContainerComponent
  ],
  styleUrls: ['./charity-management.component.scss']
})
export class CharityManagementComponent implements OnDestroy {

  @Input() set gameSetter(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.fetchCharities();
  }

  public PermissionTypes = PermissionTypes;
  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateEnumForTemplate = UIStateEnum;
  public charityCategoriesForSorting: ICharitiesByCategoryResult[] = [];
  public enableSortCharityCategory: boolean = false;
  public activeGame: IGameQueryResult | null = null;
  public charityCategories$: Observable<ICharitiesByCategoryResult[]> = this.charityService.charityCategories$.pipe(tap((categoriesP) => {
    this.charityCategoriesForSorting = categoriesP.sort((a, b) => a.Category.SortOrder - b.Category.SortOrder);
  }));
  public charitySubscription: Subscription | null = null;
  public activeCharityCategory$ = this.charityService.selectActiveCategory();

  constructor(private charityService: CharityService,
              private errorHandlingService: ErrorHandlingService,
              private snackBarService: SnackbarService) {
  }

  ngOnDestroy(): void {
    if (this.charitySubscription) {
      this.charitySubscription.unsubscribe();
    }
  }

  public updateSortingCategories() {
    this.enableSortCharityCategory = !this.enableSortCharityCategory;
    this.charityCategoriesForSorting = this.charityCategoriesForSorting
      .sort((a, b) => a.Category.SortOrder - b.Category.SortOrder);
  }

  public sortCategoriesAlphabetically() {
    this.charityCategoriesForSorting = this.charityCategoriesForSorting
      .sort((a, b) => a.Category.Name.trim().localeCompare(b.Category.Name));
    for (let i = 0; i < this.charityCategoriesForSorting.length; i++) {
      this.charityCategoriesForSorting[i].Category.SortOrder = i;
    }
  }

  submitCategoryChanges() {
    let updatedCharities = this.updateSortOrderForCategoriesList();
    let updateCharityCategorySort: IUpdateCharityCategorySortOrder = {
      charityCategories: updatedCharities,
      gameId: this.activeGame?.Id!
    };

    this.uiState = UIStateEnum.ShowLoading;

    this.charityService.updateCharityCategorySortOrder(updateCharityCategorySort).subscribe({
        next: () => {
          this.enableSortCharityCategory = false;
          this.uiState = UIStateEnum.ShowData;
          this.snackBarService.openSuccessfulSnackBar('Successfully Updated Category Sorting');
        },
        error: err => {
          this.uiState = UIStateEnum.ShowData;
          this.errorHandlingService.displayPageLevelErrorMessage(err);
        }
      }
    )
  }

  updateSortOrderForCategoriesList(): ICharityCategory[] {
    return this.charityCategoriesForSorting.map((val, index) => {
      val.Category.SortOrder = index + 1;
      return val.Category;
    });
  }

  dropCategory(event: any) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  public viewCharityCategory(categoryP: ICharitiesByCategoryResult) {
    this.charityService.setActiveCharityCategory(categoryP.CategoryId);
  }

  private fetchCharities() {
    this.charitySubscription = this.charityService.fetchCharitiesPerGameByCategory(this.activeGame?.Id!)
      .pipe(concatMap(() => {
        return this.charityCategories$;
      })).subscribe({
        next: () => {
          this.uiState = UIStateEnum.ShowData;
        },
        error: err => {
          this.uiState = UIStateEnum.ShowData;
          this.errorHandlingService.displayPageLevelErrorMessage(err);
        }
      });
  }
}
